import { message } from "antd";
import { post } from "../utils/Backend";
import { resolveAppAvatarUrl } from "../utils/resolveAppMediaUrl";
import {
  mockGetRecentSpaceVisits,
  mockGetUserByToken,
  mockListFollowSpaceCards,
  mockLoginByPhoneCode,
  mockLogoutApi,
  mockSendLoginVerificationCode,
  useH5Mock,
} from "./GlobalMock";
import type {
  AppComplianceLevelInfo,
  ComplianceLevelCode,
  CurrentUser,
  FollowSpaceCard,
  RecentSpaceEntry,
  UserInfo,
} from "./GlobalType";

const rawPlatformUserAccountSpaceId =
  process.env.REACT_APP_USER_ACCOUNT_SPACE_ID?.trim() ?? "";

function convertToUserInfo(currentUser: CurrentUser): UserInfo {
  const props = currentUser.userProps;
  const avatarRaw = props.avatarUrl;
  const avatarUrl =
    typeof avatarRaw === "string" && avatarRaw.trim() !== ""
      ? resolveAppAvatarUrl(avatarRaw.trim())
      : undefined;
  return {
    id: String(props.userId ?? props.id ?? ""),
    name: String(props.nickName ?? props.name ?? ""),
    phone: String(props.mobile ?? props.phoneNo ?? ""),
    avatarUrl,
    birthday:
      typeof props.birthday === "string"
        ? String(props.birthday).trim() || undefined
        : undefined,
    gender:
      typeof props.gender === "string"
        ? String(props.gender).trim() || undefined
        : undefined,
  };
}

const getUserByToken = async (): Promise<UserInfo | null> => {
  if (useH5Mock) {
    if (!localStorage.getItem("token")) {
      return null;
    }
    const mock = mockGetUserByToken();
    if (!mock?.token) {
      return null;
    }
    return convertToUserInfo(mock);
  }
  const res = await post<Record<string, never>, CurrentUser>(
    "/lark/app/user/getUserInfo",
    {},
    { silent: true }
  );
  if (typeof res === "string" || !res) {
    return null;
  }
  if (res.token) {
    localStorage.setItem("token", res.token);
  }
  return convertToUserInfo(res);
};

const sendLoginVerificationCode = async (
  phoneNo: string,
  captchaToken?: string
): Promise<boolean> => {
  if (useH5Mock) {
    return mockSendLoginVerificationCode(phoneNo);
  }
  const res = await post<
    { phoneNo: string; captchaToken?: string },
    boolean
  >(
    "/lark/app/sendLoginVerificationCode",
    {
      phoneNo,
      ...(captchaToken ? { captchaToken } : {}),
    },
    { silent: true }
  );
  if (typeof res === "string") {
    if (res === "1006") {
      message.error("发送过于频繁，请稍后再试");
    }
    return false;
  }
  return !!res;
};

const loginByPhoneCode = async (
  phoneNo: string,
  code: string
): Promise<UserInfo | null> => {
  if (useH5Mock) {
    const m = mockLoginByPhoneCode(phoneNo, code);
    if (!m) {
      return null;
    }
    localStorage.setItem("token", m.token);
    return convertToUserInfo(m);
  }
  const loginSpaceId = rawPlatformUserAccountSpaceId;
  if (
    !loginSpaceId ||
    (!/^\d{8}$/.test(loginSpaceId) && !/^[0-9A-Za-z]{3}$/.test(loginSpaceId))
  ) {
    message.warning("请在 .env.longfans-h5 中配置 REACT_APP_USER_ACCOUNT_SPACE_ID");
    return null;
  }
  const res = await post<
    { phoneNo: string; code: string; extraInfo: { spaceId: string } },
    CurrentUser
  >("/lark/app/user/login", {
    phoneNo,
    code: code.trim(),
    extraInfo: { spaceId: loginSpaceId },
  });
  if (typeof res === "string" || !res?.token) {
    return null;
  }
  localStorage.setItem("token", res.token);
  return convertToUserInfo(res);
};

const logoutApi = async (): Promise<void> => {
  if (useH5Mock) {
    await mockLogoutApi();
    return;
  }
  await post<Record<string, never>, boolean>("/lark/app/user/logout", {}, { silent: true });
};

const getRecentSpaceVisits = async (): Promise<RecentSpaceEntry[]> => {
  if (useH5Mock) {
    return mockGetRecentSpaceVisits();
  }
  const res = await post<Record<string, never>, RecentSpaceEntry[]>(
    "/lark/app/user/recentSubscribedSpaceVisits",
    {},
    { silent: true }
  );
  if (typeof res === "string" || !Array.isArray(res)) {
    return [];
  }
  return res;
};

const listFollowSpaceCards = async (): Promise<FollowSpaceCard[]> => {
  if (useH5Mock) {
    return mockListFollowSpaceCards();
  }
  const res = await post<Record<string, never>, FollowSpaceCard[]>(
    "/lark/app/user/myFollowSpaceCards",
    {},
    { silent: true }
  );
  if (typeof res === "string" || !Array.isArray(res)) {
    return [];
  }
  return res.filter((c) => c.favorStatus === 1);
};

const URL_COMPLIANCE_LEVEL =
  process.env.REACT_APP_COMPLIANCE_LEVEL_URL?.trim() ||
  "/lark/app/config/getComplianceLevel";

const getComplianceLevel = async (): Promise<ComplianceLevelCode> => {
  if (useH5Mock) {
    return "finance_share";
  }
  const res = await post<Record<string, never>, AppComplianceLevelInfo>(
    URL_COMPLIANCE_LEVEL,
    {},
    { silent: true }
  );
  if (typeof res === "string" || !res?.levelCode) {
    return "finance_share";
  }
  return res.levelCode;
};

export const GlobalApi = {
  getComplianceLevel,
  getUserByToken,
  sendLoginVerificationCode,
  loginByPhoneCode,
  logoutApi,
  getRecentSpaceVisits,
  listFollowSpaceCards,
};
